/**
 * BoardSampler — rejection-based sampling for flop / turn / river cards.
 *
 * Per /design/specs/5_game_engine_spec.md §4 and the constraint definitions locked
 * 2026-05-31 (game engine spec §4.1.2).
 *
 * Strategy:
 *   1. Sample a candidate from the live deck (deck minus deadCards).
 *   2. Classify the candidate against the priority-ordered category list.
 *   3. Accept if the classified category matches the requested constraint.
 *   4. Reject and retry up to MAX_REJECT_ATTEMPTS.
 *
 * Turn/river constraint matching uses priority-ordered classification:
 * FourFlush > OneLinerStraight > DoublePair > Flushing > Straightening >
 * BoardPairing > Ace > Broadway > Blank. The most structurally specific
 * category wins; a card classified as FourFlush is not also classified as
 * Flushing even though both definitions technically apply.
 */

import { type Card, type Rank, RANK_VALUE } from '@/types/card';
import type { RNG } from '@/lib/rng';
import { freshDeck, removeCards } from './deck';
import {
  flopSupportsOneLinerStraight,
  isBoardPairingCard,
  isDoublePairCard,
  isFlushingCard,
  isFourFlushCard,
  isOneLinerStraightCard,
  isStraighteningCard,
  topRank,
} from './handEvaluator';

// =============================================================================
// Constraint types
// =============================================================================

export type FlopConstraint = 'AceHigh' | 'NonAceHigh' | 'Any';

export type TurnConstraint =
  | 'Blank'
  | 'Broadway'
  | 'Straightening'
  | 'Flushing'
  | 'BoardPairing'
  | 'Ace'
  | 'OneLinerStraight'
  | 'FourFlush'
  | 'DoublePair'
  | 'Any';

export type RiverConstraint =
  | 'Blank'
  | 'Broadway'
  | 'Straightening'
  | 'Flushing'
  | 'BoardPairing'
  | 'Ace'
  | 'Any';

const MAX_REJECT_ATTEMPTS = 200;

// =============================================================================
// Flop
// =============================================================================

export function sampleFlop(
  constraint: FlopConstraint,
  deadCards: readonly Card[],
  rng: RNG,
): [Card, Card, Card] {
  for (let attempt = 0; attempt < MAX_REJECT_ATTEMPTS; attempt++) {
    const candidate = pickThreeFromDeck(deadCards, rng);
    if (flopMatches(candidate, constraint)) return candidate;
  }
  throw new Error(
    `BoardSampler: flop constraint "${constraint}" infeasible after ${MAX_REJECT_ATTEMPTS} attempts`,
  );
}

function pickThreeFromDeck(
  deadCards: readonly Card[],
  rng: RNG,
): [Card, Card, Card] {
  const live = removeCards(freshDeck(), deadCards);
  if (live.length < 3) {
    throw new Error('BoardSampler: not enough live cards to deal a flop');
  }
  const shuffled = rng.shuffle(live);
  return [shuffled[0]!, shuffled[1]!, shuffled[2]!];
}

function flopMatches(flop: [Card, Card, Card], constraint: FlopConstraint): boolean {
  if (constraint === 'Any') return true;
  const isAceHigh = topRank(flop) === 'A';
  if (constraint === 'AceHigh') return isAceHigh;
  if (constraint === 'NonAceHigh') return !isAceHigh;
  return false;
}

// =============================================================================
// Turn
// =============================================================================

export function sampleTurn(
  flop: readonly [Card, Card, Card],
  constraint: TurnConstraint,
  deadCards: readonly Card[],
  rng: RNG,
): Card {
  return sampleSingleCard({
    boardBefore: flop,
    constraint,
    deadCards,
    rng,
    classify: classifyTurnCard,
    descriptor: 'turn',
  });
}

/**
 * Classify a turn card against the priority-ordered turn category list.
 * Returns the first matching category in priority order.
 */
export function classifyTurnCard(
  flop: readonly Card[],
  turn: Card,
): TurnConstraint {
  // Conditional categories (require flop prerequisite + structural match)
  if (isFourFlushCard(flop, turn)) return 'FourFlush';
  if (
    flopSupportsOneLinerStraight(flop) &&
    isOneLinerStraightCard(flop, turn)
  ) {
    return 'OneLinerStraight';
  }
  if (isDoublePairCard(flop, turn)) return 'DoublePair';

  // General categories
  if (isFlushingCard(flop, turn)) return 'Flushing';
  if (isStraighteningCard(flop, turn)) return 'Straightening';
  if (isBoardPairingCard(flop, turn)) return 'BoardPairing';
  if (turn.rank === 'A') return 'Ace';
  if (isBroadwayRank(turn.rank)) return 'Broadway';
  return 'Blank';
}

// =============================================================================
// River
// =============================================================================

export function sampleRiver(
  flop: readonly [Card, Card, Card],
  turn: Card,
  constraint: RiverConstraint,
  deadCards: readonly Card[],
  rng: RNG,
): Card {
  const boardBefore: readonly Card[] = [...flop, turn];
  return sampleSingleCard({
    boardBefore,
    constraint,
    deadCards,
    rng,
    classify: classifyRiverCard,
    descriptor: 'river',
  });
}

/**
 * Classify a river card against the priority-ordered river category list.
 * River does not have the conditional turn-only categories.
 */
export function classifyRiverCard(
  board: readonly Card[],
  river: Card,
): RiverConstraint {
  if (isFlushingCard(board, river)) return 'Flushing';
  if (isStraighteningCard(board, river)) return 'Straightening';
  if (isBoardPairingCard(board, river)) return 'BoardPairing';
  if (river.rank === 'A') return 'Ace';
  if (isBroadwayRank(river.rank)) return 'Broadway';
  return 'Blank';
}

// =============================================================================
// Shared rejection-sampling helper
// =============================================================================

interface SampleSingleCardArgs<C extends string> {
  boardBefore: readonly Card[];
  constraint: C;
  deadCards: readonly Card[];
  rng: RNG;
  classify: (boardBefore: readonly Card[], candidate: Card) => C;
  descriptor: string;
}

function sampleSingleCard<C extends string>({
  boardBefore,
  constraint,
  deadCards,
  rng,
  classify,
  descriptor,
}: SampleSingleCardArgs<C>): Card {
  if ((constraint as string) === 'Any') {
    return pickSingleCard(deadCards, rng);
  }

  for (let attempt = 0; attempt < MAX_REJECT_ATTEMPTS; attempt++) {
    const candidate = pickSingleCard(deadCards, rng);
    if (classify(boardBefore, candidate) === constraint) return candidate;
  }
  throw new Error(
    `BoardSampler: ${descriptor} constraint "${constraint}" infeasible after ${MAX_REJECT_ATTEMPTS} attempts`,
  );
}

function pickSingleCard(deadCards: readonly Card[], rng: RNG): Card {
  const live = removeCards(freshDeck(), deadCards);
  if (live.length === 0) throw new Error('BoardSampler: no live cards remaining');
  return rng.pick(live);
}

// =============================================================================
// Helpers
// =============================================================================

const BROADWAY_RANKS: ReadonlySet<Rank> = new Set(['T', 'J', 'Q', 'K', 'A']);
function isBroadwayRank(rank: Rank): boolean {
  return BROADWAY_RANKS.has(rank);
}

/** Exposed for downstream consumers that need rank-value comparisons. */
export const _RANK_VALUE = RANK_VALUE;
